/**
 * The small staff above a staff, offering another way to play the bar.
 *
 * An ossia is an alternative, not a part: an editor's realisation of a figured bass, a
 * simpler reading of a hard passage, the way a composer played it themselves. It is drawn
 * on a short staff sitting above the one it comments on, in smaller type, for the bars it
 * has something to say about and no others, and it is never played -- the player chooses.
 *
 * The document keeps one as a voice of the bar flagged {@link Voice.ossia}, standing on the
 * staff it is offered above. That is what makes it editable at all: a caret names a line by
 * its index in `measure.voices`, so an ossia kept anywhere else would need every layer
 * between a keystroke and a note -- selection, entry, clipboard, marks, lyrics -- taught to
 * reach it. Kept as a voice, it is typed into exactly like any other line, and the few
 * places where an alternative is not the same as a second line opt out by name: playback
 * and the MIDI file skip it, the exporter writes it on a staff of its own, and the voice
 * commands here refuse to divide it further.
 *
 * Nothing here is about a keystroke. It takes a score, a caret and a selection, and answers
 * with the command to run and where the caret should then stand, or with a sentence saying
 * why there is neither.
 */

import { copyOfEvent, voice as newVoice } from "../document/create";
import type { EventId } from "../document/ids";
import { capacityOf, measureIndexOf } from "../document/query";
import type { Command } from "../commands/types";
import type { MusicEvent, PartMeasure, Score } from "../document/types";
import type { Caret } from "./entry-place";
import type { VoiceChange } from "./lines";
import { asOne, barsFor, kept, ossiasOn, silentEvents, voicesOn, whereIs } from "./lines";
import type { Selection } from "./selection";

/** Whether the staff the caret stands on offers an ossia in the bar it stands in. */
export function offeredOn(score: Score, caret: Caret | null): boolean {
  const at = caret === null ? null : whereIs(score, caret);
  return at !== null && ossiasOn(at.measure, at.staff).length > 0;
}

/**
 * What a new ossia starts out holding: the line it is an alternative to, copied.
 *
 * An ossia is written against something, and the something is almost always the line the
 * caret is standing in -- the passage to be fingered differently, simplified, or realised.
 * Starting from a copy of it means the usual edit is a few notes changed rather than a bar
 * typed from nothing, and starting from silence would be a bar of rests offered as an
 * alternative to music, which is not an alternative to anything.
 *
 * The copies carry new ids, and no note among them says it is drawn on another staff: an
 * ossia has a staff of its own and there is nowhere to reach across to. A tie at either
 * edge of the bar is cut, because the music it tied to is on the staff below.
 */
function seedFrom(measure: PartMeasure, staff: number, ordinal: number): readonly MusicEvent[] {
  const on = voicesOn(measure, staff);
  const index = on[Math.min(ordinal, on.length - 1)];
  const source = index === undefined ? undefined : measure.voices[index];
  if (source === undefined || source.events.length === 0) return [];
  const last = source.events.length - 1;
  return source.events.map((event, at) => copyOfEvent(event, at === 0, at === last, 0));
}

/**
 * Offer an ossia above the staff the caret stands on, wherever the edit reaches.
 *
 * A bar that already offers one is left alone rather than given a second: two small staves
 * above one staff is not an ossia, it is two, and a reader has no way to tell which of them
 * the bar is about. The caret comes out standing at the start of the new line, in the first
 * bar the edit reached.
 */
export function add(score: Score, caret: Caret | null, selection: Selection): VoiceChange | string {
  if (caret === null) return "click where the ossia should go first";
  const at = whereIs(score, caret);
  if (at === null) return "the caret is not standing in a voice";
  if (at.ossia) return "that line is the ossia";

  const commands: Command[] = [];
  let landing: Caret | null = null;

  for (const measureId of barsFor(score, caret, selection)) {
    const measure = at.part.measures[measureId];
    if (measure === undefined || ossiasOn(measure, at.staff).length > 0) continue;
    const on = voicesOn(measure, at.staff);
    const index = (on[on.length - 1] ?? -1) + 1;
    const capacity = capacityOf(score, measureIndexOf(score, measureId));
    const seed = seedFrom(measure, at.staff, at.ordinal);
    const voices = [...measure.voices];
    voices.splice(
      index,
      0,
      newVoice(at.staff, seed.length > 0 ? seed : silentEvents(capacity), true),
    );
    landing ??= { ...caret, measureId, voiceIndex: index, onset: 0 };
    commands.push({
      kind: "setPartMeasure",
      partId: at.part.id,
      measureId,
      measure: { ...measure, voices },
    });
  }

  const command = asOne(commands, "offer an ossia");
  if (command === null || landing === null) return "that staff already offers an ossia there";
  return { command, caret: landing };
}

/**
 * Take the ossia off the staff the caret stands on, wherever the edit reaches.
 *
 * The caret may be standing in the ossia or in the music under it: both mean the same staff,
 * and asking for the alternative to go while reading the line it was written against is the
 * ordinary gesture. Everything hung on the notes going with it goes too, exactly as it does
 * when a voice is taken out -- a dynamic anchored to one of them would be invisible but
 * still exported, and a slur with one end in it has nowhere to be drawn.
 */
export function remove(
  score: Score,
  caret: Caret | null,
  selection: Selection,
): VoiceChange | string {
  if (caret === null) return "click in the bar the ossia is in first";
  const at = whereIs(score, caret);
  if (at === null) return "the caret is not standing in a voice";

  const gone = new Set<EventId>();
  const commands: Command[] = [];
  let landing: Caret | null = null;
  const ordinal = at.ossia ? 0 : at.ordinal;

  for (const measureId of barsFor(score, caret, selection)) {
    const measure = at.part.measures[measureId];
    if (measure === undefined) continue;
    const index = ossiasOn(measure, at.staff)[0];
    const voice = index === undefined ? undefined : measure.voices[index];
    if (index === undefined || voice === undefined) continue;

    for (const event of voice.events) gone.add(event.id);
    const voices = measure.voices.slice();
    voices.splice(index, 1);
    const left = voicesOn({ ...measure, voices }, at.staff);
    landing ??= {
      ...caret,
      measureId,
      voiceIndex: left[Math.min(ordinal, left.length - 1)] ?? 0,
      onset: 0,
    };
    commands.push({
      kind: "setPartMeasure",
      partId: at.part.id,
      measureId,
      measure: { ...measure, voices, directions: kept(measure.directions, gone) },
    });
  }

  const spanners = at.part.spanners.filter((span) => !gone.has(span.from) && !gone.has(span.to));
  if (spanners.length !== at.part.spanners.length) {
    commands.push({ kind: "setSpanners", partId: at.part.id, spanners });
  }

  const command = asOne(commands, "take the ossia away");
  if (command === null || landing === null) return "there is no ossia there to take away";
  return { command, caret: landing };
}
